import React, { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  createStyles,
  makeStyles,
  Typography,
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp';
import theme from '../../themes/theme';
import { useTranslation } from '../../hooks/useTranslation';
import AccountDropdown from '../account-dropdown';
import colors from '../../themes/main-colors';
import useGetAccountType from '../../hooks/useGetAccountType';
import { AccountType } from '../../__generated__/pge-types';
import { RES_TIP_CATEGORIES } from './resTips';
import { COM_TIP_CATEGORIES } from './comTips';

export type SvgIcon = React.ComponentType<React.SVGProps<SVGSVGElement>>;

export interface Tip {
  Icon: SvgIcon;
  titleKey: string;
  detailsKey: string;
  renterFriendly: boolean;
}

export interface TipCategory {
  titleKey: string;
  tips: Tip[];
}

const useStyles = makeStyles(() =>
  createStyles({
    heading: {
      fontSize: theme.spacing(4.5),
      lineHeight: theme.typography.pxToRem(28),
      fontWeight: 'bold',
      letterSpacing: '0.720px',
      fontFamily: 'Forma-DJR-Display',
      color: colors.lightCyan,
      [theme.breakpoints.down('sm')]: {
        fontSize: theme.spacing(3),
        marginBottom: '16px',
      },
    },
    accountBox: {
      margin: '16px 0px 32px',
      [theme.breakpoints.down('sm')]: {
        margin: '16px 0px 20px',
      },
    },
    category: {
      backgroundColor: colors.white,
      borderTop: `4px solid ${colors.sparkBlue}`,
      marginBottom: theme.spacing(1.5),
      '&:before': {
        display: 'none',
      },
      '&.Mui-expanded': {
        margin: theme.spacing(0, 0, 1.5),
      },
    },
    categorySummary: {
      padding: theme.spacing(1.5, 3),
      minHeight: 'auto',
      '&.Mui-expanded': {
        minHeight: 'auto',
      },
      '& .MuiAccordionSummary-content, & .MuiAccordionSummary-content.Mui-expanded': {
        margin: 0,
        flexDirection: 'column',
      },
      '& .MuiAccordionSummary-expandIcon': {
        padding: theme.spacing(1),
        color: colors.sparkBlue,
      },
    },
    categoryDetails: {
      padding: 0,
      flexDirection: 'column',
      borderTop: `1px solid ${colors.lightGray1}`,
    },
    listTitle: {
      fontSize: theme.spacing(2.5),
      fontWeight: 'bold',
      fontFamily: 'Forma-DJR-Display',
      color: colors.lightCyan,
    },
    listCount: {
      marginTop: theme.spacing(0.25),
      color: colors.noirBlur,
    },
    tip: {
      padding: theme.spacing(3),
      borderBottom: `1px solid ${colors.lightGray1}`,
      transition: 'transform 0.2s ease, box-shadow 0.2s ease',
      '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: `0 6px 16px ${colors.boxShadowLight}`,
        borderRadius: '4px',
      },
      '&:last-child': {
        borderBottom: 'none',
      },
    },
    tipHeader: {
      display: 'grid',
      gridTemplateColumns: 'auto 1fr 150px',
      gridTemplateAreas: '"icon title toggle"',
      alignItems: 'center',
      columnGap: theme.spacing(3),
      [theme.breakpoints.down('sm')]: {
        gridTemplateColumns: '1fr',
        gridTemplateAreas: '"title" "toggle"',
        rowGap: theme.spacing(2),
      },
    },
    iconCircle: {
      gridArea: 'icon',
      width: 96,
      height: 96,
      borderRadius: '50%',
      backgroundColor: colors.cloudGrey,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
      overflow: 'hidden',
      '& svg': {
        width: 56,
        height: 56,
      },
      [theme.breakpoints.down('sm')]: {
        width: 72,
        height: 72,
        '& svg': {
          width: 42,
          height: 42,
        },
      },
    },
    tipTitle: {
      gridArea: 'title',
      fontSize: theme.spacing(2.5),
      fontWeight: 'bold',
      fontFamily: 'Forma-DJR-Display',
      color: colors.lightCyan,
    },
    toggle: {
      gridArea: 'toggle',
      justifySelf: 'end',
      display: 'inline-flex',
      alignItems: 'center',
      gap: theme.spacing(0.5),
      padding: 0,
      border: 'none',
      background: 'none',
      cursor: 'pointer',
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
      fontWeight: 'bold',
      color: colors.sparkBlue,
      fontFamily: 'inherit',
      [theme.breakpoints.down('sm')]: {
        justifySelf: 'start',
      },
    },
    details: {
      marginTop: theme.spacing(3),
    },
    whyHeading: {
      fontWeight: 'bold',
      color: colors.lightCyan,
      marginBottom: theme.spacing(1.5),
    },
    whyText: {
      color: colors.noirBlur,
      lineHeight: 1.5,
    },
    sectionSubtext: {
      fontFamily: 'Untitled-Sans',
      marginBottom: '10px',
    },
  }),
);

const getLinkTarget = (uri: string) =>
  /^(mailto|tel):/i.test(uri) ? '_self' : '_blank';

export default function MyWaysToSave({ path }: { path?: string }) {
  const { t } = useTranslation();
  const classes = useStyles();
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const { accountTypeData } = useGetAccountType({});
  const isResAccount =
    accountTypeData?.getAccountDetails &&
    accountTypeData?.getAccountDetails[0]?.accountType === AccountType.Res;
  let introSlug: string = '';
  let categories: TipCategory[] = [];
  if (isResAccount) {
    introSlug = 'WTS_INTRO_RES';
    categories = RES_TIP_CATEGORIES;
  } else {
    introSlug = 'WTS_INTRO_COM';
    categories = COM_TIP_CATEGORIES;
  }

  const inlineRichTNewTab = (key: string) => (
    <ReactMarkdown
      disallowedTypes={['paragraph']}
      unwrapDisallowed
      linkTarget={getLinkTarget}
    >
      {t(key)}
    </ReactMarkdown>
  );

  const toggleTip = (key: string) =>
    setExpanded(prev => ({ ...prev, [key]: !prev[key] }));

  return (
    <>
      <Typography className={classes.heading}>
        {t('MY_WAYS_TO_SAVE')}
      </Typography>
      <Box className={classes.accountBox}>
        <AccountDropdown />
      </Box>
      <Box>
        <Typography className={classes.sectionSubtext}>
          {inlineRichTNewTab(introSlug)}
        </Typography>
        {categories.map(category => (
          <Accordion
            key={category.titleKey}
            className={classes.category}
            square
          >
            <AccordionSummary
              className={classes.categorySummary}
              expandIcon={<ExpandMoreIcon />}
              aria-controls={`${category.titleKey}-content`}
              id={`${category.titleKey}-header`}
            >
              <Typography className={classes.listTitle}>
                {t(category.titleKey)}
              </Typography>
              <Typography className={classes.listCount}>
                {String(category.tips.length) + ' ' + t('WTS_TIPS_COUNT')}
              </Typography>
            </AccordionSummary>
            <AccordionDetails className={classes.categoryDetails}>
              {category.tips.map(
                ({ Icon, titleKey, detailsKey, renterFriendly }) => {
                  const isOpen = Boolean(expanded[titleKey]);
                  return (
                    <Box key={titleKey} className={classes.tip}>
                      <Box className={classes.tipHeader}>
                        {/* <Box className={classes.iconCircle}>
                        <Icon aria-hidden="true" />
                      </Box> */}
                        <Typography className={classes.tipTitle}>
                          {t(titleKey)}
                        </Typography>
                        <button
                          type="button"
                          className={classes.toggle}
                          aria-expanded={isOpen}
                          onClick={() => toggleTip(titleKey)}
                        >
                          {isOpen
                            ? t('WTS_HIDE_DETAILS')
                            : t('WTS_SHOW_DETAILS')}
                          {isOpen ? (
                            <KeyboardArrowUpIcon fontSize="small" />
                          ) : (
                            <KeyboardArrowDownIcon fontSize="small" />
                          )}
                        </button>
                      </Box>
                      {isOpen && (
                        <Box className={classes.details}>
                          <Typography className={classes.whyText}>
                            {inlineRichTNewTab(detailsKey)}
                          </Typography>
                          {renterFriendly && (
                            <>
                              <br />
                              <Typography
                                component="em"
                                className={classes.whyText}
                              >
                                {t('RENTER_FRIENDLY')}
                              </Typography>
                            </>
                          )}
                        </Box>
                      )}
                    </Box>
                  );
                },
              )}
            </AccordionDetails>
          </Accordion>
        ))}
      </Box>
    </>
  );
}
